import { useState } from 'react';
import { Plus, Pencil, XCircle } from 'lucide-react';
import { Button, Badge, Modal, Input, EmptyState } from '../../../ui';
import { toast } from '../../../ui/toast';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import {
  useCompanies,
  useBankAccounts,
  useCreateBankAccount,
  useUpdateBankAccount,
  useDeactivateBankAccount,
} from '../../../hooks/useFinancialOps';
import {
  useOpenFinanceAccounts,
  useOpenFinanceStatus,
  useOpenFinanceSync,
} from '../hooks/useOpenFinance';
import {
  ERROS_OPEN_FINANCE,
  MCPAI_CONSENTIMENTOS_URL,
  erroDaBusca,
  resumoDaBusca,
} from '../utils/openFinance';

const ACCOUNT_TYPE_LABELS: Record<string, string> = {
  CHECKING: 'Conta Corrente',
  SAVINGS: 'Poupança',
  PAYMENT: 'Conta de Pagamento',
};

interface AccountFormData {
  companyId: string;
  name: string;
  bankName: string;
  bankCode: string;
  agency: string;
  accountNumber: string;
  accountType: string;
  pixKey: string;
  notes: string;
  openFinanceAccountId: string;
}

const emptyForm = (companyId: string): AccountFormData => ({
  companyId,
  name: '',
  bankName: '',
  bankCode: '',
  agency: '',
  accountNumber: '',
  accountType: 'CHECKING',
  pixKey: '',
  notes: '',
  openFinanceAccountId: '',
});

export function ContasPage() {
  const { data: companiesData } = useCompanies({ active: true });
  const companies = companiesData?.data ?? [];

  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const { data: accountsData, isLoading } = useBankAccounts(selectedCompanyId);
  const createAccount = useCreateBankAccount();
  const updateAccount = useUpdateBankAccount();
  const deactivateAccount = useDeactivateBankAccount();

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<AccountFormData>(emptyForm(''));

  const ofAccounts = useOpenFinanceAccounts(showModal, 'BANK');
  const ofStatus = useOpenFinanceStatus();
  const ofSync = useOpenFinanceSync();
  const statusDe = (id: string) => ofStatus.data?.find((s) => s.bankAccountId === id);

  function celulaOpenFinance(account: { id: string; openFinanceAccountId?: string | null }) {
    if (!account.openFinanceAccountId) {
      return '—';
    }
    const st = statusDe(account.id);
    return (
      <span>
        {st?.lastSyncAt ? `busca ${new Date(st.lastSyncAt).toLocaleString('pt-BR')}` : 'vinculada'}
        {st?.lastError && (
          <span className="block text-red-600 text-xs">
            {ERROS_OPEN_FINANCE[st.lastError] ?? st.lastError}
          </span>
        )}
      </span>
    );
  }

  const accounts = accountsData?.data ?? [];

  function buscarAgora() {
    ofSync.mutate(undefined, {
      onSuccess: (r) => {
        if (!r || r.length === 0) {
          toast.error(
            'Nenhuma conta vinculada ao Open Finance. Edite uma conta e escolha a "Conta Open Finance".'
          );
          return;
        }
        const { texto, comErro } = resumoDaBusca(r);
        if (comErro) {
          toast.error(texto);
        } else {
          toast.success(texto);
        }
      },
      onError: (err) => toast.error(erroDaBusca(err)),
    });
  }

  function openCreate() {
    setEditingId(null);
    setForm(emptyForm(selectedCompanyId));
    setShowModal(true);
  }

  function openEdit(account: any) {
    setEditingId(account.id);
    setForm({
      companyId: account.companyId,
      name: account.name ?? '',
      bankName: account.bankName ?? '',
      bankCode: account.bankCode ?? '',
      agency: account.agency ?? '',
      accountNumber: account.accountNumber ?? '',
      accountType: account.accountType ?? 'CHECKING',
      pixKey: account.pixKey ?? '',
      notes: account.notes ?? '',
      openFinanceAccountId: account.openFinanceAccountId ?? '',
    });
    setShowModal(true);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const vinculo = form.openFinanceAccountId || null;
    const payload: Record<string, any> = { ...form };
    Object.keys(payload).forEach((k) => {
      if (payload[k] === '') {
        delete payload[k];
      }
    });
    payload.openFinanceAccountId = vinculo;
    if (editingId) {
      await updateAccount.mutateAsync({ id: editingId, data: payload });
    } else {
      await createAccount.mutateAsync(payload);
    }
    setShowModal(false);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div className="flex-1 max-w-xs">
          <label className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5">
            Empresa
          </label>
          <select
            className="h-9 w-full border border-border rounded-md px-3 text-sm bg-surface text-ink"
            value={selectedCompanyId}
            onChange={(e) => setSelectedCompanyId(e.target.value)}
          >
            <option value="">Selecione uma empresa...</option>
            {companies.map((c: any) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        {selectedCompanyId && (
          <div className="mt-5">
            <Button onClick={openCreate} size="sm">
              <Plus size={16} className="mr-1" /> Nova Conta
            </Button>
          </div>
        )}
        <div className="mt-5 flex items-center gap-3">
          <Button size="sm" variant="ghost" onClick={buscarAgora} loading={ofSync.isPending}>
            Buscar agora
          </Button>
          <a
            href={MCPAI_CONSENTIMENTOS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-ink-secondary underline hover:text-ink"
          >
            Gerenciar consentimentos no mcp.ai
          </a>
        </div>
      </div>

      {!selectedCompanyId ? (
        <EmptyState
          title="Selecione uma empresa"
          body="Escolha uma empresa para ver as contas bancárias."
        />
      ) : isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : accounts.length === 0 ? (
        <EmptyState title="Nenhuma conta cadastrada" body="Clique em Nova Conta para cadastrar." />
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-subtle text-ink-secondary">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Nome</th>
                <th className="px-4 py-2 text-left font-medium">Banco</th>
                <th className="px-4 py-2 text-left font-medium">Tipo</th>
                <th className="px-4 py-2 text-left font-medium">Agência / Conta</th>
                <th className="px-4 py-2 text-left font-medium">Chave PIX</th>
                <th className="px-4 py-2 text-left font-medium">Open Finance</th>
                <th className="px-4 py-2 text-left font-medium">Status</th>
                <th className="px-4 py-2 text-left font-medium">Ações</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((account: any) => (
                <tr key={account.id} className="border-t border-border hover:bg-subtle/50">
                  <td className="px-4 py-3 font-medium text-ink">{account.name}</td>
                  <td className="px-4 py-3 text-ink-secondary">{account.bankName}</td>
                  <td className="px-4 py-3 text-ink-secondary">
                    {ACCOUNT_TYPE_LABELS[account.accountType] ?? account.accountType}
                  </td>
                  <td className="px-4 py-3 text-ink-secondary">
                    {account.agency ? `${account.agency} / ${account.accountNumber ?? '—'}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-ink-secondary">{account.pixKey ?? '—'}</td>
                  <td className="px-4 py-3 text-ink-secondary">{celulaOpenFinance(account)}</td>
                  <td className="px-4 py-3">
                    <Badge tone={account.active ? 'accent' : 'neutral'}>
                      {account.active ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEdit(account)}
                        className="text-ink-secondary hover:text-ink-primary"
                        title="Editar"
                      >
                        <Pencil size={16} />
                      </button>
                      {account.active && (
                        <button
                          onClick={() => deactivateAccount.mutate(account.id)}
                          className="text-ink-secondary hover:text-red-500"
                          title="Desativar"
                        >
                          <XCircle size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editingId ? 'Editar Conta' : 'Nova Conta Bancária'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Nome da Conta *"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            required
          />
          <Input
            label="Banco *"
            value={form.bankName}
            onChange={(e) => setForm((f) => ({ ...f, bankName: e.target.value }))}
            required
          />
          <Input
            label="Código do Banco"
            value={form.bankCode}
            onChange={(e) => setForm((f) => ({ ...f, bankCode: e.target.value }))}
            placeholder="ex: 001"
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Agência"
              value={form.agency}
              onChange={(e) => setForm((f) => ({ ...f, agency: e.target.value }))}
            />
            <Input
              label="Número da Conta"
              value={form.accountNumber}
              onChange={(e) => setForm((f) => ({ ...f, accountNumber: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5">
              Tipo de Conta
            </label>
            <select
              className="h-9 w-full border border-border rounded-md px-3 text-sm bg-surface text-ink"
              value={form.accountType}
              onChange={(e) => setForm((f) => ({ ...f, accountType: e.target.value }))}
            >
              {Object.entries(ACCOUNT_TYPE_LABELS).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              htmlFor="of-account"
              className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5"
            >
              Conta Open Finance
            </label>
            <select
              id="of-account"
              className="h-9 w-full border border-border rounded-md px-3 text-sm bg-surface text-ink"
              value={form.openFinanceAccountId}
              onChange={(e) => setForm((f) => ({ ...f, openFinanceAccountId: e.target.value }))}
            >
              <option value="">Nenhuma</option>
              {(ofAccounts.data ?? [])
                .filter(
                  (a) =>
                    a.type === 'BANK' &&
                    a.companyId === form.companyId &&
                    (!a.linkedBankAccountId || a.linkedBankAccountId === editingId)
                )
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    {`${a.companyName ?? 'empresa?'} · ${a.name ?? 'conta'} · final ${a.last4 ?? '—'}`}
                  </option>
                ))}
            </select>
          </div>
          <Input
            label="Chave PIX"
            value={form.pixKey}
            onChange={(e) => setForm((f) => ({ ...f, pixKey: e.target.value }))}
          />
          <Input
            label="Observações"
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          />
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button type="submit" loading={createAccount.isPending || updateAccount.isPending}>
              {editingId ? 'Salvar' : 'Criar'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
